import { Fragment, type ReactNode } from "react"
import { useI18n } from "@/i18n"

type TranslateProps = {
  k: string
  params?: Record<string, string>
  className?: string
}

/**
 * Parses simple markdown syntax (**bold**) and converts to React elements
 * This is a safe alternative to dangerouslySetInnerHTML
 */
function parseMarkdown(text: string): ReactNode[] {
  const parts: ReactNode[] = []
  let currentIndex = 0
  let partKey = 0

  // Match **bold** pattern
  const boldRegex = /\*\*([^*]+)\*\*/g
  let match = boldRegex.exec(text)

  while (match !== null) {
    // Add text before the match
    if (match.index > currentIndex) {
      parts.push(
        <Fragment key={partKey++}>
          {text.slice(currentIndex, match.index)}
        </Fragment>,
      )
    }

    // Add the bold text
    parts.push(<strong key={partKey++}>{match[1]}</strong>)

    currentIndex = match.index + match[0].length
    match = boldRegex.exec(text)
  }

  // Add remaining text
  if (currentIndex < text.length) {
    parts.push(<Fragment key={partKey++}>{text.slice(currentIndex)}</Fragment>)
  }

  return parts.length > 0 ? parts : [text]
}

export const Translate = ({ k, params, className }: TranslateProps) => {
  const { t } = useI18n()

  let text = t(k)

  // Apply params if provided
  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      text = text.replace(`{${paramKey}}`, String(paramValue))
    })
  }

  const parsed = parseMarkdown(text)

  if (className) {
    return <span className={className}>{parsed}</span>
  }

  return <>{parsed}</>
}
